import { useState, useEffect, useCallback } from "react";
import { addFarmer, getFarmers, generateFarmerCode, getMilkEntries, getCattleFoodEntries, getTransactions } from "../services/firebaseService";
import { FaUser, FaPhone, FaMapMarkerAlt, FaUserPlus, FaUsers, FaIdCard, FaTimes } from 'react-icons/fa';

export default function FarmerRegistration() {
  const [farmers, setFarmers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    village: "",
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [farmerEntries, setFarmerEntries] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [farmerFoodEntries, setFarmerFoodEntries] = useState([]);
  const [allFoodEntries, setAllFoodEntries] = useState([]);
  const [activeTab, setActiveTab] = useState('milk');
  const [allTransactions, setAllTransactions] = useState([]);

  const loadFarmers = useCallback(async () => {
    try {
      setLoading(true);
      const [farmersData, entriesData, foodEntriesData, transactionsData] = await Promise.all([getFarmers(), getMilkEntries(), getCattleFoodEntries(), getTransactions()]);
      setFarmers(farmersData);
      setAllEntries(entriesData);
      setAllFoodEntries(foodEntriesData);
      setAllTransactions(transactionsData);
    } catch (error) {
      console.error('Error loading farmers:', error);
      alert(`Error loading farmers: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFarmers();
  }, [loadFarmers]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile || !form.village) return;
    
    try {
      setSubmitting(true);
      const newFarmer = {
        ...form,
        code: generateFarmerCode(farmers),
      };
      const savedFarmer = await addFarmer(newFarmer);
      setFarmers([...farmers, savedFarmer]);
      setForm({ name: "", mobile: "", village: "" });
    } catch (error) {
      console.error('Error adding farmer:', error);
      alert('Error registering farmer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFarmerClick = (farmer) => {
    const entries = allEntries.filter(e => e.farmerCode === farmer.code);
    const dayWiseData = {};
    entries.forEach(entry => {
      if (!dayWiseData[entry.date]) {
        dayWiseData[entry.date] = [];
      }
      dayWiseData[entry.date].push(entry);
    });
    setFarmerEntries(dayWiseData);
    
    const foodEntries = allFoodEntries.filter(e => e.farmerCode === farmer.code);
    const dayWiseFoodData = {};
    foodEntries.forEach(entry => {
      if (!dayWiseFoodData[entry.date]) {
        dayWiseFoodData[entry.date] = [];
      }
      dayWiseFoodData[entry.date].push(entry);
    });
    setFarmerFoodEntries(dayWiseFoodData);
    
    setSelectedFarmer(farmer);
    setActiveTab('milk');
  };

  const closeFarmerModal = () => {
    setSelectedFarmer(null);
    setFarmerEntries({});
    setFarmerFoodEntries({});
  };

  return (
    <div className="container fade-in">
      {/* Stats Cards */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-icon green"><FaUsers /></div>
          <div className="stat-info">
            <span className="stat-number">{farmers.length}</span>
            <span className="stat-label">Total Farmers</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon blue"><FaMapMarkerAlt /></div>
          <div className="stat-info">
            <span className="stat-number">{[...new Set(farmers.map(f => f.village))].length}</span>
            <span className="stat-label">Villages</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange"><FaIdCard /></div>
          <div className="stat-info">
            <span className="stat-number">{farmers.length > 0 ? farmers[farmers.length - 1]?.code : 'N/A'}</span>
            <span className="stat-label">Last Code</span>
          </div>
        </div>
      </div>

      {/* Registration Form */}
      <div className="card">
        <div className="card-header-custom">
          <div className="card-title-icon"><FaUserPlus /></div>
          <div>
            <h2>Register New Farmer</h2>
            <p>Add a new farmer to the system</p>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="enhanced-form">
          <div className="form-grid">
            <div className="form-field">
              <label><FaUser className="field-icon" /> Farmer Name</label>
              <input type="text" name="name" placeholder="Enter farmer's full name" value={form.name} onChange={handleChange} required />
            </div>
            <div className="form-field">
              <label><FaPhone className="field-icon" /> Mobile Number</label>
              <input type="tel" name="mobile" placeholder="Enter 10-digit mobile number" value={form.mobile} onChange={handleChange} required />
            </div>
            <div className="form-field">
              <label><FaMapMarkerAlt className="field-icon" /> Village</label>
              <input type="text" name="village" placeholder="Enter village name" value={form.village} onChange={handleChange} required />
            </div>
          </div>
          <button type="submit" className="btn btn-primary btn-large" disabled={submitting}>
            {submitting ? (<><span className="btn-spinner"></span> Registering...</>) : (<><FaUserPlus /> Register Farmer</>)}
          </button>
        </form>
      </div>

      {/* Farmers List */}
      <div className="card">
        <div className="card-header-custom">
          <div className="card-title-icon"><FaUsers /></div>
          <div>
            <h2>Registered Farmers</h2>
            <p>{farmers.length} farmers in the system</p>
          </div>
        </div>
        
        {loading ? (
          <div className="loading-state">
            <div className="loader"></div>
            <p>Loading farmers...</p>
          </div>
        ) : farmers.length === 0 ? (
          <div className="empty-state-enhanced">
            <div className="empty-icon">👨‍🌾</div>
            <h3>No Farmers Yet</h3>
            <p>Register your first farmer using the form above</p>
          </div>
        ) : (
          <div className="farmers-grid">
            {farmers.map((f, index) => (
              <div key={`farmer-${f.code}-${index}`} className="farmer-card" style={{ animationDelay: `${index * 0.05}s`, cursor: 'pointer' }} onClick={() => handleFarmerClick(f)}>
                <div className="farmer-avatar">{f.name.charAt(0).toUpperCase()}</div>
                <div className="farmer-details">
                  <h4>{f.name}</h4>
                  <div className="farmer-meta">
                    <span className="farmer-code"><FaIdCard /> {f.code}</span>
                    <span className="farmer-phone"><FaPhone /> {f.mobile}</span>
                    <span className="farmer-village"><FaMapMarkerAlt /> {f.village}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedFarmer && (
        <div className="modal-overlay" onClick={closeFarmerModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>{selectedFarmer.name}</h2>
                <p>Code: {selectedFarmer.code} | Mobile: {selectedFarmer.mobile} | Village: {selectedFarmer.village}</p>
              </div>
              <button onClick={closeFarmerModal} className="modal-close"><FaTimes /></button>
            </div>

            <div className="modal-tabs">
              <button className={`tab-btn ${activeTab === 'milk' ? 'active' : ''}`} onClick={() => setActiveTab('milk')}>🥛 Milk Entries</button>
              <button className={`tab-btn ${activeTab === 'food' ? 'active' : ''}`} onClick={() => setActiveTab('food')}>🌾 Cattle Food</button>
              <button className={`tab-btn ${activeTab === 'bill' ? 'active' : ''}`} onClick={() => setActiveTab('bill')}>💳 Bill</button>
            </div>

            <div className="modal-body">
              {activeTab === 'milk' && (
                Object.keys(farmerEntries).length === 0 ? (
                  <div className="empty-message">No milk entries found</div>
                ) : (
                  Object.keys(farmerEntries).sort().reverse().map(date => {
                    const dayEntries = farmerEntries[date];
                    const dayTotal = dayEntries.reduce((sum, e) => sum + e.quantity, 0);
                    const dayAmount = dayEntries.reduce((sum, e) => sum + (e.quantity * e.price), 0);
                    return (
                      <div key={date} className="day-section">
                        <div className="day-header">
                          <h4>{new Date(date).toLocaleDateString('en-IN', { weekday: 'long', month: 'short', day: 'numeric' })}</h4>
                          <div className="day-total">
                            <span className="qty">{dayTotal.toFixed(1)} L</span>
                            <span className="amount">₹{dayAmount.toFixed(2)}</span>
                          </div>
                        </div>
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Animal</th>
                              <th>Time</th>
                              <th>Qty (L)</th>
                              <th>Price (₹/L)</th>
                              <th>Total (₹)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dayEntries.map((entry, idx) => (
                              <tr key={idx}>
                                <td>{entry.animalType}</td>
                                <td>{entry.time}</td>
                                <td>{entry.quantity.toFixed(1)}</td>
                                <td>{entry.price.toFixed(2)}</td>
                                <td className="total-cell">{(entry.quantity * entry.price).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })
                )
              )}

              {activeTab === 'food' && (
                Object.keys(farmerFoodEntries).length === 0 ? (
                  <div className="empty-message">No cattle food entries found</div>
                ) : (
                  Object.keys(farmerFoodEntries).sort().reverse().map(date => {
                    const dayFoodEntries = farmerFoodEntries[date];
                    const dayFoodTotal = dayFoodEntries.reduce((sum, e) => sum + (e.totalAmount || 0), 0);
                    return (
                      <div key={date} className="day-section">
                        <div className="day-header">
                          <h4>{new Date(date).toLocaleDateString('en-IN', { weekday: 'long', month: 'short', day: 'numeric' })}</h4>
                          <div className="day-total">
                            <span className="amount">₹{dayFoodTotal.toFixed(2)}</span>
                          </div>
                        </div>
                        {dayFoodEntries.map((entry, entryIdx) => (
                          <table key={entryIdx} className="data-table">
                            <thead>
                              <tr>
                                <th>Food Item</th>
                                <th>Qty</th>
                                <th>Price</th>
                                <th>Total (₹)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entry.items && entry.items.map((item, itemIdx) => (
                                <tr key={itemIdx}>
                                  <td>{item.itemName || item.itemId}</td>
                                  <td>{item.quantity}</td>
                                  <td>₹{item.price.toFixed(2)}</td>
                                  <td className="total-cell">₹{(item.total || item.quantity * item.price).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ))}
                      </div>
                    );
                  })
                )
              )}

              {activeTab === 'bill' && (
                (() => {
                  const farmerTransactions = allTransactions.filter(t => t.farmerCode === selectedFarmer.code);
                  return farmerTransactions.length === 0 ? (
                    <div className="empty-message">No bill transactions found</div>
                  ) : (
                    <div>
                      {farmerTransactions.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).map((transaction, idx) => (
                        <div key={idx} className="day-section">
                          <div className="day-header">
                            <h4>{new Date(transaction.timestamp).toLocaleDateString('en-IN', { weekday: 'long', month: 'short', day: 'numeric' })}</h4>
                            <div className="day-total">
                              <span className="amount">₹{transaction.amountGiven?.toFixed(2) || '0.00'}</span>
                            </div>
                          </div>
                          <table className="data-table">
                            <tbody>
                              <tr>
                                <td style={{ fontWeight: 'bold' }}>Period</td>
                                <td>{transaction.fromDate} to {transaction.toDate}</td>
                              </tr>
                              <tr>
                                <td style={{ fontWeight: 'bold' }}>Amount Given</td>
                                <td>₹{transaction.amountGiven?.toFixed(2) || '0.00'}</td>
                              </tr>
                              <tr>
                                <td style={{ fontWeight: 'bold' }}>Remaining</td>
                                <td className={transaction.remainingAmount < 0 ? 'negative' : 'positive'}>₹{transaction.remainingAmount?.toFixed(2) || '0.00'}</td>
                              </tr>
                              <tr>
                                <td style={{ fontWeight: 'bold' }}>Time</td>
                                <td>{new Date(transaction.timestamp).toLocaleTimeString('en-IN')}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  );
                })()
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
